/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Display formatting for ListSelectionDialog rows.
 *
 * The OnePass options endpoint labels a channel "202 CNN" — number first, then
 * call sign — while Figma sets the row the other way round: the call sign in
 * TV/Medium/H4 (42/54) with the number trailing it in TV/Medium/H6 (34/39) and
 * a dimmer grey. Rows that are not channels ("All") have no number at all.
 *
 * The engine hands the overlay one flat string per option, so the split happens
 * here rather than in the view model: it is a typography concern, and the
 * original label is still what round-trips back to the engine on save.
 */

/** A row label divided into the two type sizes the design uses. */
export interface IItemLabelParts {
  /** Channel call sign or plain label, set large ("CNN", "All"). */
  name: string;
  /**
   * Channel number, set small and dim after the name. `null` when the label
   * carries no number, in which case the row is a single piece of text.
   */
  number: string | null;
}

/**
 * Channel number and the whitespace separating it from the call sign. Both
 * orders are accepted because the service formats the label per market — the
 * OnePass payloads seen so far lead with the number ("202 CNN"), while others
 * trail it ("ABC 001"). Numbers may carry a sub-channel part, e.g. "7.1 ABC".
 */
const LEADING_CHANNEL_NUMBER: RegExp = /^(\d+(?:\.\d+)?)\s+/;

/** A word that is a channel number and nothing else, e.g. "001" or "7.1". */
const CHANNEL_NUMBER_ONLY: RegExp = /^\d+(?:\.\d+)?$/;

/**
 * Split a list row label into the call sign and channel number Figma sets
 * separately.
 *
 * @param label - Raw option label from the engine, e.g. "202 CNN".
 * @returns The two parts; `number` is `null` for labels carrying no number.
 *
 * @example
 * splitItemLabel("202 CNN"); // { name: "CNN", number: "202" }
 * splitItemLabel("ABC 001"); // { name: "ABC", number: "001" }
 * splitItemLabel("7.1 ABC"); // { name: "ABC", number: "7.1" }
 * splitItemLabel("All");     // { name: "All", number: null }
 */
export function splitItemLabel(label: string): IItemLabelParts {
  const leading: RegExpMatchArray | null = label.match(LEADING_CHANNEL_NUMBER);
  if (leading !== null && leading[1] !== undefined) {
    return { name: label.slice(leading[0].length), number: leading[1] };
  }

  // Matched by hand rather than by an anchored regex, which SonarJS rejects
  // for its backtracking cost on a leading `\s+`.
  const lastSpace: number = label.lastIndexOf(" ");
  const tail: string = label.slice(lastSpace + 1);
  if (lastSpace > 0 && CHANNEL_NUMBER_ONLY.test(tail)) {
    return { name: label.slice(0, lastSpace).trimEnd(), number: tail };
  }

  return { name: label, number: null };
}
